/**
 * This file is part of the "Awaken Media" project.
 *
 * (c) 2017 - CanalPlus International
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { PureComponent } from 'react';
import styled from 'styled-components';
import get from 'lodash/get';
import { shallowEqual } from 'recompose';

import { ZOOM_LEVELS } from 'shared/constants/map';
import {
  Map,
  MapDirections,
  MapUserMarker,
  MapMessage
} from 'shared/components/presentational/Map';
import mediaQueries from 'shared/helpers/styled-components/mediaQueries';
import { getBoundsFromCoordinates, getDirections } from 'shared/helpers/map';
import { TABS_KEYS } from '../constants';
import MapShopMarkers from './components/MapShopMarkers';
import _List from './components/List';
import _Filters from './components/Filters';
import _ListTabs from './components/ListTabs';

const MobileMapMessage = styled(MapMessage)`
  display: none;
  ${mediaQueries.toTablet`
    display: block;
  `};
`;
/*
 * Main wrapper should be a section
 * in order to inherit margins
 */
const Wrapper = styled.section`
  display: flex;
  flex-direction: column;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  ${mediaQueries.toTablet`
    margin-left: 0;
    margin-right: 0;
  `};
`;

const List = styled(_List)`
  position: relative;
  width: 420px;

  ${mediaQueries.toTablet`
    width: 100%;
    border-right: none;
  `};
`;

const ListTabs = styled(_ListTabs)`
  display: none;
  margin: 0 auto 20px;

  ${mediaQueries.toTablet`
    display:flex;
  `};
`;

const Filters = styled(_Filters)`
  margin-top: 30px;
  margin-bottom: 30px;
`;

const Header = styled.div`
  display: block;
`;

const MapWrapper = styled.div`
  flex: 1;
  position: relative;
  min-height: 754px; // height for 6 items + pagination
  ${mediaQueries.toPhablet`
    min-height: 520px; // mobile size
  `};
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;

  ${mediaQueries.toTablet`
    border-right: none;
    border-left: none;
    
    ${MapWrapper}{
      display: none;
    }
    
    ${List}{
      display: none;
    }
  
    ${MapWrapper}{
      display: ${props =>
        props.selectedTab === TABS_KEYS.MAP ? 'block' : 'none'};
    }
    ${List}{
      display: ${props =>
        props.selectedTab === TABS_KEYS.LIST ? 'block' : 'none'};
    }
  `};
`;

class ShopMap extends PureComponent {
  state = {
    selectedTab: TABS_KEYS.LIST,
    directions: null,
    directionsError: false,
    directionsPending: false
  };

  componentDidMount() {
    const { fetchShops, url, changePage, filterShops } = this.props;
    fetchShops(url).then(() => {
      const { selectedShop, filters } = this.props;

      if (selectedShop) {
        // pre-fill filters with values from selected shop
        filters
          .filter(({ filterOn }) => filterOn !== 'name')
          .map(({ filterOn }) => {
            filterShops(filterOn, get(selectedShop, filterOn, null));
            return true;
          });
        changePage(null); // will set page for selected shop
      }
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { fetchShops, url } = nextProps;
    fetchShops(url);
  }

  UNSAFE_componentWillUpdate(nextProps) {
    const simplify = list => list && list.map(({ id }) => id);
    const {
      fetchShopsDetails,
      shopsMap,
      selectedShop = { id: null },
      selectShop
    } = this.props;
    const {
      shopsMap: nextShopsMap,
      selectedShop: nextSelectedShop
    } = nextProps;
    if (nextSelectedShop) {
      if (nextSelectedShop.id !== selectedShop.id) {
        fetchShopsDetails(nextSelectedShop.URLdetail, nextSelectedShop.id);
      }
      if (
        !shallowEqual(simplify(shopsMap), simplify(nextShopsMap)) &&
        !simplify(nextShopsMap).includes(nextSelectedShop.id)
      ) {
        selectShop(null);
      }
    }
  }

  get selectedShopCenter() {
    const { shopsMap, selectedShop } = this.props;
    const shop = shopsMap.find(s => s.id === get(selectedShop, 'id', null));
    return shop
      ? { lat: parseFloat(shop.latitude, 10) + 0.004, lng: shop.longitude }
      : null;
  }

  handleTabChange = tab => {
    this.setState(() => ({
      selectedTab: tab
    }));
  };

  handleFilterChange = (filterOn, value) => {
    const { filterShops, changePage } = this.props;
    filterShops(filterOn, value);
    changePage(null);
  };

  handleSelectShop = (shop = null) => {
    const { selectShop } = this.props;
    const { handleTabChange } = this;

    if (!shop) {
      selectShop(null);
      this.setState({ directions: null });
    } else {
      selectShop(shop.id);
      handleTabChange(TABS_KEYS.MAP);
    }
  };

  handleDirections = destination => {
    const { coordinates, fetchCoordinates, t } = this.props;
    let geolocationPromise;

    this.setState({ directionsPending: true }, () => {
      if (coordinates == null) {
        geolocationPromise = fetchCoordinates();
      } else {
        geolocationPromise = Promise.resolve(coordinates);
      }

      geolocationPromise
        .then(origin => {
          /* global google */
          getDirections(google)(
            { lat: origin.latitude, lng: origin.longitude },
            { lat: destination.latitude, lng: destination.longitude }
          )
            .then(directions => {
              this.setState({ directions, directionsPending: false });
            })
            .catch(() => {
              this.setState({
                directionsError: t('blocks.shops.map.error.route'),
                directionsPending: false
              });
            });
        })
        .catch(() => {
          this.setState({
            directionsError: t('blocks.shops.map.error.position'),
            directionsPending: false
          });
        });
    });
  };

  handleDirectionsError = () => {
    this.setState({ directionsError: false });
  };

  render() {
    const {
      buttonGeolocation,
      filters,
      shopsList,
      shopsMap,
      selectedShop,
      isSelectedShopLoading,
      currentPage,
      totalPages,
      changePage: handlePageChange,
      userCoordinates,
      fetchingCoordinatesPending,
      countryCoordinates,
      fetchCoordinates,
      t
    } = this.props;

    const selectedShopId = selectedShop && selectedShop.id;

    const {
      selectedTab,
      directions,
      directionsError,
      directionsPending
    } = this.state;

    const { handleSelectShop, handleDirections, handleDirectionsError } = this;

    return (
      <Wrapper>
        <Header>
          <Filters
            buttonGeolocation={buttonGeolocation}
            filters={filters}
            onFilterChange={this.handleFilterChange}
            fetchCoordinates={fetchCoordinates}
            fetchingCoordinatesPending={fetchingCoordinatesPending}
            t={t}
          />
          <ListTabs
            defaultCheck={selectedTab}
            onTabChange={this.handleTabChange}
            t={t}
          />
        </Header>
        <Content selectedTab={selectedTab}>
          <List
            currentPage={currentPage}
            totalPages={totalPages}
            onChangePage={handlePageChange}
            shops={shopsList}
            selectedShopId={selectedShopId}
            onItemClick={handleSelectShop}
            t={t}
          />
          <MapWrapper>
            {!shopsMap.length && (
              <MobileMapMessage
                message={t('blocks.shops.map.error.noResult')}
              />
            )}
            {shopsMap && directionsError && (
              <MapMessage
                message={directionsError}
                onClose={handleDirectionsError}
              />
            )}
            <Map
              defaultCenter={countryCoordinates}
              userCoordinates={userCoordinates}
              fetchCoordinates={fetchCoordinates}
              zoom={selectedShop && ZOOM_LEVELS.SELECTED}
              center={this.selectedShopCenter}
              bounds={getBoundsFromCoordinates(shopsMap)}
            >
              <MapDirections directions={directions} />
              <MapUserMarker coordinates={userCoordinates} />
              <MapShopMarkers
                shops={shopsMap}
                selectedShopId={selectedShopId}
                isSelectedShopLoading={isSelectedShopLoading}
                onMarkerClick={handleSelectShop}
                onInfoWindowClose={handleSelectShop}
                handleDirections={handleDirections}
                directionsPending={directionsPending}
                t={t}
              />
            </Map>
          </MapWrapper>
        </Content>
      </Wrapper>
    );
  }
}

export default ShopMap;
